import _ from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast, ToastContainer } from 'react-toastify'
import SqaureLoader from '../../../components/Loader/SqaureLoader'
import ExportToExcel from '../../../components/exportExcel/ExportToExcel'
import { getVisitReports, getVisitReportUsers } from '../../../services/Rapports/VisitReportApi'
import VisitReportDetail from './VisitReportDetail'
import { OBJET_LABELS, SCALE, SECTION_TITLES, colorForNote, labelForNote } from './visitReportConstants'
import './rapportVisite.css'

/** Note globale pondérée (hors RAS) à partir des agrégats par critère. */
const computeGlobalNote = (ratings) => {
    let sum = 0
    let count = 0
    ratings.forEach((r) => {
        sum += 4 * r.n4 + 3 * r.n3 + 2 * r.n2 + 1 * r.n1
        count += Number(r.n4) + Number(r.n3) + Number(r.n2) + Number(r.n1)
    })
    return count === 0 ? null : sum / count
}

const formatNote = (v) => (v === null || v === undefined ? '–' : Number(v).toFixed(1).replace('.', ','))

export default function RapportVisite() {
    const [t] = useTranslation('global')

    // ---- Filtres ----
    const [datedebut, setdatedebut] = useState(moment().startOf('month').format('YYYY-MM-DD'))
    const [datefin, setdatefin] = useState(moment().format('YYYY-MM-DD'))
    const [userCode, setuserCode] = useState('all')
    const [users, setusers] = useState([])
    const [search, setsearch] = useState('')

    // ---- Données ----
    const [loading, setloading] = useState(true)
    const [reports, setreports] = useState([])
    const [stats, setstats] = useState({})
    const [ratings, setratings] = useState([])
    const [objets, setobjets] = useState([])
    const [topics, settopics] = useState([])
    const [opportunities, setopportunities] = useState([])
    const [selectedId, setselectedId] = useState(null)

    const traitement = (d1 = datedebut, d2 = datefin, u = userCode) => {
        setloading(true)
        getVisitReports(d1, d2, u)
            .then((result) => {
                if (result?.data?.success === 1) {
                    setreports(result.data.reports || [])
                    setstats(result.data.stats || {})
                    setratings(result.data.ratings || [])
                    setobjets(result.data.objets || [])
                    settopics(result.data.topics || [])
                    setopportunities(result.data.opportunities || [])
                } else {
                    toast(t('Impossible de charger les rapports. Vérifiez la connexion au serveur.'), { type: 'error', position: 'top-right' })
                }
            })
            .catch((err) => console.log(err))
            .finally(() => setloading(false))
    }

    useEffect(() => {
        getVisitReportUsers().then((result) => {
            if (result?.data?.success === 1) setusers(result.data.data || [])
        })
        traitement()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSearch = () => {
        if (datedebut > datefin) {
            toast(t('La date de début doit être antérieure à la date de fin.'), { type: 'warning', position: 'top-right' })
            return
        }
        traitement(datedebut, datefin, userCode)
    }

    // ---- Calculs dérivés ----
    const globalNote = useMemo(() => computeGlobalNote(ratings), [ratings])
    const ratingsBySection = useMemo(() => _.groupBy(ratings, 'section'), [ratings])
    const tauxAccompagnement = stats.nb_rapports > 0 ? Math.round((stats.nb_accompagnes / stats.nb_rapports) * 100) : 0

    const filteredReports = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return reports
        return reports.filter((r) =>
            `${r.nompdv || ''} ${r.pdv_code || ''} ${r.user || ''}`.toLowerCase().includes(q)
        )
    }, [reports, search])

    return (
        <div className="vrr">
            <ToastContainer />

            <div className="page-header">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/rapports"><i className="fe fe-life-buoy mr-1" /> {t('rapports.Rapport')}</a></li>
                    <li className="breadcrumb-item active" aria-current="page">{t('Rapports de visite')}</li>
                </ol>
            </div>

            {/* ===================== EN-TÊTE + FILTRES ===================== */}
            <header className="vrr-head">
                <div>
                    <h1 className="vrr-title">{t('Rapports de visite')}</h1>
                    <p className="vrr-subtitle">
                        {t('Visites terrain saisies sur mobile par les superviseurs, chefs d\'enseigne et responsables GMS.')}
                    </p>
                </div>

                <div className="vrr-filters" role="search">
                    <label className="vrr-field">
                        <span>{t('Du')}</span>
                        <input type="date" value={datedebut} onChange={(e) => setdatedebut(e.target.value)} />
                    </label>
                    <label className="vrr-field">
                        <span>{t('Au')}</span>
                        <input type="date" value={datefin} onChange={(e) => setdatefin(e.target.value)} />
                    </label>
                    <label className="vrr-field vrr-field-wide">
                        <span>{t('Commercial')}</span>
                        <select value={userCode} onChange={(e) => setuserCode(e.target.value)}>
                            <option value="all">{t('Tous les commerciaux')}</option>
                            {users.map((u) => (
                                <option key={u.user_code} value={u.user_code}>{u.nom}</option>
                            ))}
                        </select>
                    </label>
                    <button type="button" className="vrr-btn" onClick={onSearch} disabled={loading}>
                        <i className="ti-search mr-1" /> {t('Afficher')}
                    </button>
                </div>
            </header>

            {loading ? (
                <div className="card"><SqaureLoader /></div>
            ) : stats.nb_rapports === 0 || !stats.nb_rapports ? (
                <div className="vrr-empty">
                    <h2>{t('Aucun rapport sur cette période')}</h2>
                    <p>{t('Élargissez les dates ou choisissez « Tous les commerciaux ». Les rapports apparaissent ici après la synchronisation des téléphones.')}</p>
                </div>
            ) : (
                <>
                    {/* ===================== CHIFFRES CLÉS ===================== */}
                    <section className="vrr-kpis" aria-label={t('Chiffres clés')}>
                        <div className="vrr-kpi">
                            <strong>{stats.nb_rapports}</strong>
                            <span>{t('rapports de visite')}</span>
                        </div>
                        <div className="vrr-kpi">
                            <strong>{stats.nb_pdv}</strong>
                            <span>{t('points de vente visités')}</span>
                        </div>
                        <div className="vrr-kpi">
                            <strong>{tauxAccompagnement}<small>%</small></strong>
                            <span>{t('visites accompagnées')}</span>
                        </div>
                        <div className="vrr-kpi">
                            <strong style={{ color: colorForNote(globalNote) }}>
                                {formatNote(globalNote)}<small>/4</small>
                            </strong>
                            <span>{t('note merchandising moyenne')}</span>
                        </div>
                    </section>

                    {/* ===================== SANTÉ DU RAYON ===================== */}
                    <section className="vrr-panel vrr-health">
                        <div className="vrr-panel-head">
                            <h2>{t('Santé du rayon NATILAIT')}</h2>
                            <ul className="vrr-legend">
                                {SCALE.map((s) => (
                                    <li key={s.note}><i style={{ background: s.color }} />{s.note} {t(s.label)}</li>
                                ))}
                            </ul>
                        </div>

                        {Object.keys(SECTION_TITLES).map((section) =>
                            ratingsBySection[section] ? (
                                <div className="vrr-health-group" key={section}>
                                    <h3>{section} {t(SECTION_TITLES[section])}</h3>
                                    {ratingsBySection[section].map((r) => (
                                        <HealthRow key={`${r.section}-${r.critere_index}`} row={r} t={t} />
                                    ))}
                                </div>
                            ) : null
                        )}
                        {ratings.length === 0 && (
                            <p className="vrr-muted">{t('Aucune évaluation merchandising saisie sur cette période.')}</p>
                        )}
                    </section>

                    {/* ===================== TENDANCES ===================== */}
                    <div className="vrr-trends">
                        <BarList
                            title={t('Objets de visite')}
                            items={objets.map((o) => ({ key: o.objet_code, label: t(OBJET_LABELS[o.objet_code] || o.libelle), value: o.total }))}
                            total={stats.nb_rapports}
                            empty={t('Aucun objet de visite renseigné.')}
                        />
                        <BarList
                            title={t('Sujets abordés en magasin')}
                            items={topics.map((tp) => ({ key: tp.sujet, label: t(tp.sujet), value: tp.total }))}
                            total={stats.nb_rapports}
                            empty={t('Aucun sujet coché.')}
                        />
                        <BarList
                            title={t('Opportunités détectées')}
                            items={opportunities.map((op) => ({
                                key: op.opportunite,
                                label: t(op.opportunite),
                                value: op.total,
                                note: op.prioritaires > 0 ? `${op.prioritaires} ${t('prioritaire(s)')}` : null,
                            }))}
                            total={stats.nb_rapports}
                            empty={t('Aucune opportunité signalée.')}
                        />
                    </div>

                    {/* ===================== LISTE DES RAPPORTS ===================== */}
                    <section className="vrr-panel">
                        <div className="vrr-panel-head">
                            <h2>{t('Détail des visites')} <span className="vrr-count">{filteredReports.length}</span></h2>
                            <div className="vrr-table-tools">
                                <input
                                    type="search"
                                    className="vrr-search"
                                    placeholder={t('Rechercher un PDV ou un commercial')}
                                    value={search}
                                    onChange={(e) => setsearch(e.target.value)}
                                />
                                <ExportToExcel id="table-visit-reports" namefile="rapports_visite" t={t} />
                            </div>
                        </div>

                        <div className="vrr-table-wrap">
                            <table id="table-visit-reports" className="vrr-table">
                                <thead>
                                    <tr>
                                        <th>{t('Date')}</th>
                                        <th>{t('Commercial')}</th>
                                        <th>{t('Point de vente')}</th>
                                        <th>{t('Objets de la visite')}</th>
                                        <th className="vrr-center">{t('Accompagné')}</th>
                                        <th className="vrr-center">{t('notes')}</th>
                                        <th className="vrr-center">{t('Opp. prioritaires')}</th>
                                        <th className="vrr-center">{t('Photos')}</th>
                                        <th><span className="sr-only">{t('Actions')}</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredReports.map((r) => {
                                        const codes = r.objets ? r.objets.split(',') : []
                                        return (
                                            <tr key={r.id} onClick={() => setselectedId(r.id)} className="vrr-row">
                                                <td className="vrr-nowrap">
                                                    <b>{moment(r.visit_date).format('DD/MM/YYYY')}</b>
                                                    <div className="vrr-muted">{moment(r.visit_date).format('HH:mm')}</div>
                                                </td>
                                                <td>{r.user || r.user_code}</td>
                                                <td>
                                                    <b>{r.nompdv || r.pdv_code}</b>
                                                    {r.nompdv && <div className="vrr-muted">{r.pdv_code}</div>}
                                                </td>
                                                <td>
                                                    <div className="vrr-chips">
                                                        {codes.map((c) => <span key={c} className="vrr-chip">{t(OBJET_LABELS[c] || c)}</span>)}
                                                    </div>
                                                </td>
                                                <td className="vrr-center">{r.accompagnement === 1 ? t('Oui') : t('Non')}</td>
                                                <td className="vrr-center">
                                                    <span
                                                        className="vrr-note"
                                                        style={{ '--note-color': colorForNote(r.note_moyenne) }}
                                                        title={t(labelForNote(r.note_moyenne))}
                                                    >
                                                        {formatNote(r.note_moyenne)}
                                                    </span>
                                                </td>
                                                <td className="vrr-center">{r.nb_opp_prioritaires > 0 ? <b>{r.nb_opp_prioritaires}</b> : '–'}</td>
                                                <td className="vrr-center">{r.nb_photos > 0 ? r.nb_photos : '–'}</td>
                                                <td className="vrr-right">
                                                    <button
                                                        type="button"
                                                        className="vrr-link"
                                                        onClick={(e) => { e.stopPropagation(); setselectedId(r.id) }}
                                                    >
                                                        {t('Ouvrir')}
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    {filteredReports.length === 0 && (
                                        <tr><td colSpan={9} className="vrr-center vrr-muted">{t('Aucune visite ne correspond à la recherche.')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}

            {selectedId && (
                <VisitReportDetail id={selectedId} t={t} onClose={() => setselectedId(null)} />
            )}
        </div>
    )
}

/* ---------------------------------------------------------------------------------- */

/** Une ligne de la carte "Santé du rayon" : libellé, répartition des notes, moyenne. */
function HealthRow({ row, t }) {
    const total = Number(row.total) || 1
    return (
        <div className="vrr-health-row">
            <span className="vrr-health-label">{t(row.critere)}</span>
            <div className="vrr-dist" role="img"
                aria-label={SCALE.map((s) => `${s.label} : ${row['n' + s.note]}`).join(', ')}>
                {SCALE.map((s) => {
                    const n = Number(row['n' + s.note]) || 0
                    return n > 0 ? (
                        <span key={s.note} style={{ flexGrow: n / total, background: s.color }}
                            title={`${t(s.label)} : ${n}`}>
                            {n / total >= 0.12 ? n : ''}
                        </span>
                    ) : null
                })}
            </div>
            <span className="vrr-note vrr-note-lg" style={{ '--note-color': colorForNote(row.moyenne) }}
                title={t(labelForNote(row.moyenne))}>
                {formatNote(row.moyenne)}
            </span>
        </div>
    )
}

/** Liste de barres horizontales (fréquence sur le nombre de rapports). */
function BarList({ title, items, total, empty }) {
    const max = Math.max(1, ...items.map((i) => Number(i.value)))
    return (
        <section className="vrr-panel vrr-barlist">
            <h2>{title}</h2>
            {items.length === 0 ? (
                <p className="vrr-muted">{empty}</p>
            ) : (
                <ul>
                    {items.map((i) => (
                        <li key={i.key}>
                            <div className="vrr-bar-text">
                                <span>{i.label}</span>
                                <b>{i.value}<small> / {total}</small></b>
                            </div>
                            <div className="vrr-bar"><span style={{ width: `${(Number(i.value) / max) * 100}%` }} /></div>
                            {i.note && <div className="vrr-bar-note">{i.note}</div>}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    )
}
